import React from 'react';
import { Platform, View } from 'react-native';
import Animated, {
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { Photo } from '@/models/Photo';
import { ImageViewerPane } from './ImageViewerPane';
import { DESKTOP_IMAGE_SCALE } from './ImageViewerConstants';
import type { IPaneSlots } from './usePaneSlots';

interface IImageViewerCarouselProps {
  photos: Photo[] | undefined;
  // Per-slot photo index. Each slot (0..2) holds the photo at this index.
  // When a navigation commits, ONE slot's pageIndex rotates to the new far
  // side (off-screen), so the visible center pane keeps the same URI across
  // the commit and there is zero pixel change at center.
  slotPageIndices: [number, number, number];
  // Slot index considered "active" (visible at center). Used by the pane
  // component for desktop cursor styling.
  activeSlotIndex: 0 | 1 | 2;
  // Available screen dimensions. paneWidth = full screen width; paneHeight =
  // full screen height. The carousel intentionally uses the full screen (no
  // status-bar inset) so the image's vertical center matches the morph overlay
  // and the side prev/next arrows, which all live in screen-coordinate space.
  paneWidth: number;
  paneHeight: number;
  isDesktop: boolean;
  // Carousel-level SVs.
  trackTranslateX: SharedValue<number>;
  trackTranslateY: SharedValue<number>;
  carouselOpacity: SharedValue<number>;
  isPanning: SharedValue<boolean>;
  // Per-pane SV bags (stable across re-renders).
  paneSlots: IPaneSlots;
  allowImageDownload?: boolean;
  onImageLoadError?: (uri: string, message?: string) => void;
  onImageLoadEnd?: (uri: string) => void;
}

export const ImageViewerCarousel: React.FC<IImageViewerCarouselProps> = ({
  photos,
  slotPageIndices,
  activeSlotIndex,
  paneWidth,
  paneHeight,
  isDesktop,
  trackTranslateX,
  trackTranslateY,
  carouselOpacity,
  isPanning,
  paneSlots,
  allowImageDownload = false,
  onImageLoadError,
  onImageLoadEnd,
}) => {
  // Single absolute transform on the 3-pane track. Each pane positions itself
  // within the track via a static transform: translateX(slotPageIndex * paneWidth).
  // At rest, trackTranslateX = -activeIndex * paneWidth → the slot whose
  // pageIndex matches the active photo sits at screen center.
  const trackStyle = useAnimatedStyle(() => {
    const style: any = {
      opacity: carouselOpacity.value,
      transform: [
        { translateX: trackTranslateX.value },
        { translateY: trackTranslateY.value },
      ],
    };
    if (Platform.OS === 'web') {
      style.willChange = 'transform, opacity';
      style.backfaceVisibility = 'hidden';
    }
    return style;
  });

  // On desktop the image is scaled down via padding inside each pane wrapper.
  const desktopScale = isDesktop ? DESKTOP_IMAGE_SCALE : 1;
  const imagePaneWidth = paneWidth * desktopScale;
  const imagePaneHeight = paneHeight * desktopScale;
  const horizontalPadding = (paneWidth - imagePaneWidth) / 2;
  const verticalPadding = (paneHeight - imagePaneHeight) / 2;

  return (
    <Animated.View
      style={[
        {
          position: 'absolute',
          top: 0,
          left: 0,
          width: paneWidth,
          height: paneHeight,
        },
        trackStyle,
      ]}
      pointerEvents="box-none"
    >
      {([0, 1, 2] as const).map((slotIndex) => {
        const pageIdx = slotPageIndices[slotIndex];
        const photo =
          photos && pageIdx >= 0 && pageIdx < photos.length
            ? photos[pageIdx]
            : null;
        const uri = photo?.FullSizeUrl ?? null;
        return (
          <View
            key={slotIndex}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: paneWidth,
              height: paneHeight,
              paddingHorizontal: horizontalPadding,
              paddingVertical: verticalPadding,
              transform: [{ translateX: pageIdx * paneWidth }],
            }}
            pointerEvents="box-none"
          >
            <ImageViewerPane
              uri={uri}
              paneWidth={imagePaneWidth}
              paneHeight={imagePaneHeight}
              isDesktop={isDesktop}
              paneSlot={paneSlots.slots[slotIndex]}
              isActive={slotIndex === activeSlotIndex}
              isPanning={isPanning}
              allowImageDownload={allowImageDownload}
              onLoadError={onImageLoadError}
              onLoadEnd={onImageLoadEnd}
            />
          </View>
        );
      })}
    </Animated.View>
  );
};
